// src/components/Alerts/AlertSettingsInline.js

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { Settings as SettingsIcon, Save as SaveIcon, Info as InfoIcon } from '@mui/icons-material';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';
import { DEFAULT_ALERT_THRESHOLDS } from '../../utils/alertUtils';

const AlertSettingsInline = ({ userId, showAsana = true }) => {
  const [strategicDays, setStrategicDays] = useState(DEFAULT_ALERT_THRESHOLDS.strategicContextDays.toString());
  const [financialsDays, setFinancialsDays] = useState(DEFAULT_ALERT_THRESHOLDS.financialsDays.toString());
  const [asanaDays, setAsanaDays] = useState(DEFAULT_ALERT_THRESHOLDS.asanaDays.toString());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const initialized = useRef(false);
  const originalValues = useRef({});

  // Load user settings
  useEffect(() => {
    const loadSettings = async () => {
      if (!userId || initialized.current) return;
      
      try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data();
          const settings = data.alertSettings || {};
          
          const strategic = (settings.strategicContextDays ?? DEFAULT_ALERT_THRESHOLDS.strategicContextDays).toString();
          const financials = (settings.financialsDays ?? DEFAULT_ALERT_THRESHOLDS.financialsDays).toString();
          const asana = (settings.asanaDays ?? DEFAULT_ALERT_THRESHOLDS.asanaDays).toString();
          
          setStrategicDays(strategic);
          setFinancialsDays(financials);
          setAsanaDays(asana);
          
          originalValues.current = { strategic, financials, asana };
          initialized.current = true;
        } else {
          // No user doc yet, use defaults
          originalValues.current = { 
            strategic: DEFAULT_ALERT_THRESHOLDS.strategicContextDays.toString(), 
            financials: DEFAULT_ALERT_THRESHOLDS.financialsDays.toString(), 
            asana: DEFAULT_ALERT_THRESHOLDS.asanaDays.toString() 
          };
          initialized.current = true;
        }
      } catch (error) {
        console.error('Error loading alert settings:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadSettings();
  }, [userId]);

  // Track changes
  useEffect(() => {
    if (!initialized.current) return;
    
    const changed = 
      strategicDays !== originalValues.current.strategic ||
      financialsDays !== originalValues.current.financials ||
      asanaDays !== originalValues.current.asana;
    
    setHasChanges(changed);
  }, [strategicDays, financialsDays, asanaDays]);

  const handleSave = async () => {
    if (!userId) return;
    
    setSaving(true);
    try {
      const userRef = doc(db, 'users', userId);
      const settingsToSave = {
        strategicContextDays: parseInt(strategicDays, 10) || DEFAULT_ALERT_THRESHOLDS.strategicContextDays,
        financialsDays: parseInt(financialsDays, 10) || DEFAULT_ALERT_THRESHOLDS.financialsDays
      };
      
      if (showAsana) {
        settingsToSave.asanaDays = parseInt(asanaDays, 10) || DEFAULT_ALERT_THRESHOLDS.asanaDays;
      }

      await updateDoc(userRef, {
        alertSettings: settingsToSave
      });
      
      originalValues.current = {
        strategic: strategicDays,
        financials: financialsDays,
        asana: asanaDays
      };
      setHasChanges(false);
      toast.success('Alert thresholds saved');
    } catch (error) {
      console.error('Error saving alert settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <SettingsIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" color="text.secondary">
          Alert Thresholds (days)
        </Typography>
        <Tooltip title="Get alerted when client data becomes stale. Strategic = days since last questionnaire or Strategic Pulse update. Financials = days since last financial document was processed.">
          <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
        </Tooltip>
        {hasChanges && (
          <Tooltip title="Save changes">
            <IconButton size="small" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" color="primary" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          label="Strategic"
          type="number"
          size="small"
          value={strategicDays}
          onChange={(e) => setStrategicDays(e.target.value)}
          inputProps={{ min: 1, max: 365 }}
          sx={{ width: 85 }}
        />
        <TextField
          label="Financials"
          type="number"
          size="small"
          value={financialsDays}
          onChange={(e) => setFinancialsDays(e.target.value)}
          inputProps={{ min: 1, max: 365 }}
          sx={{ width: 85 }}
        />
         {showAsana && (
          <TextField
            label="Asana"
            type="number"
            size="small"
            value={asanaDays}
            onChange={(e) => setAsanaDays(e.target.value)}
            inputProps={{ min: 1, max: 365 }}
            sx={{ width: 85 }}
          />
        )}
      </Box>
    </Box>
  );
};

export default AlertSettingsInline;